import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import Helmet from 'react-helmet';

import {
  DecorativeLink,
  CalloutLink,
  AccordionItem,
  Heading,
  Section,
  SectionCard,
  PageBanner,
  ContactInformation,
  prefixInternalLinks,
  GatsbyLink
} from '@budget-monorepo/shared-ui';
import Layout from '../components/Layout';
import bannerImage from '../images/budget-h1-banner.png';
import HelmetData from '../components/HelmetData';

// eslint-disable-next-line react/prefer-stateless-function
const IndexPage = (props) => {
  const { data } = props;
  const {
    mainNav, content, site, content: {
      header, intro, sections, contactHeading, contactInformation
    }
  } = data;
  const sectionCardNav = mainNav.navItems;
  // eslint-disable-next-line camelcase
  const { modifiedTime, mg_mod_date, mg_mod_datetime } = content.parent;
  const { siteUrl, buildTime } = site.siteMetadata;

  const layoutProps = {
    activeIndex: 0,
    pre: () => (
      <PageBanner title={header.title} size="overlay" color="blue" backgroundImage={bannerImage}>
        <span className="ma__page-banner__callout ma__page-banner__callout--primary">{header.date}</span>
        {
          header.links.map((link, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <GatsbyLink to={link.href} key={`headerLinks.${i}`} type="decorative">{link.text}</GatsbyLink>
          ))
        }
      </PageBanner>
    ),
    dataTimeStamp: modifiedTime,
    buildTimeStamp: buildTime
  };

  /*
   * Bind section cards data from mainnav and data/index.json
   *
   * SectionCard takes in the following props
   *  title: { text: card header text, href: card header href },
   *  description: text render under the card title, (passed from data/index.json)
   *  seeAll: { text: see all link text, href: see all link href }, (passed from data/index.json)
   *  children: anything to be rendered inside of a section card
   *    In this example, section card props take in an array of links can be rendered as either DecorativeLink or CalloutLink mapping string component prop to the actual component.
   *    links: [
   *      link[0]: { component: component string prop name, props: { text: link text, href: link href } },
   *      link[1], link[2], link[3] ... ]
   *
  */
  const SectionCardProps = [{
    title: { text: sectionCardNav[0].text, href: sectionCardNav[0].href },
    links: [
      { component: 'decorativeLink', props: { text: sectionCardNav[0].subNav[0].text, href: sectionCardNav[0].subNav[0].href } },
      { component: 'decorativeLink', props: { text: sectionCardNav[0].subNav[1].text, href: sectionCardNav[0].subNav[1].href } },
      { component: 'decorativeLink', props: { text: sectionCardNav[0].subNav[2].text, href: sectionCardNav[0].subNav[2].href } },
      { component: 'calloutLink', props: { text: sectionCardNav[0].subNav[3].text, href: sectionCardNav[0].subNav[3].href } },
      { component: 'calloutLink', props: { text: sectionCardNav[0].subNav[4].text, href: sectionCardNav[0].subNav[4].href } },
      { component: 'calloutLink', props: { text: sectionCardNav[0].subNav[5].text, href: sectionCardNav[0].subNav[5].href } }
    ],
    ...sections[0]
  }, {
    title: { text: sectionCardNav[1].text, href: sectionCardNav[1].href },
    links: [
      { component: 'calloutLink', props: { text: sectionCardNav[1].subNav[0].text, href: sectionCardNav[1].subNav[0].href } },
      { component: 'calloutLink', props: { text: sectionCardNav[1].subNav[1].text, href: sectionCardNav[1].subNav[1].href } },
      { component: 'decorativeLink', props: { text: sectionCardNav[1].subNav[2].text, href: sectionCardNav[1].subNav[2].href } }
    ],
    ...sections[1]
  }, {
    title: { text: sectionCardNav[2].text, href: sectionCardNav[2].href },
    links: [
      { component: 'decorativeLink', props: { text: sectionCardNav[2].subNav[0].text, href: sectionCardNav[2].subNav[0].href } },
      { component: 'decorativeLink', props: { text: sectionCardNav[2].subNav[1].text, href: sectionCardNav[2].subNav[1].href } },
      { component: 'decorativeLink', props: { text: sectionCardNav[2].subNav[2].text, href: sectionCardNav[2].subNav[2].href } },
      { component: 'decorativeLink', props: { text: sectionCardNav[2].subNav[3].text, href: sectionCardNav[2].subNav[3].href } },
      { component: 'decorativeLink', props: { text: sectionCardNav[2].subNav[4].text, href: sectionCardNav[2].subNav[4].href } },
      { component: 'decorativeLink', props: { text: sectionCardNav[2].subNav[5].text, href: sectionCardNav[2].subNav[5].href } },
      { component: 'decorativeLink', props: { text: sectionCardNav[2].subNav[6].text, href: sectionCardNav[2].subNav[6].href } }
    ],
    ...sections[2]
  }];

  // To allow more components to be mapped to the string component prop, import the component and add a key (string name) and value (React component name, starting with a capital letter)
  const components = {
    decorativeLink: DecorativeLink,
    calloutLink: CalloutLink
  };
  const sectionNum = SectionCardProps.length > 3 ? 3 : SectionCardProps.length;
  // metadata for the page
  const metaTitle = 'Home';
  const metaDescription = intro.paragraph;
  const searchTarget = `${siteUrl}/search?query={search_term_string}`;
  return(
    <Fragment>
      <HelmetData {...{
        metaTitle,
        metaDescription,
        metaType: 'dataset',
        mg_mod_date,
        mg_mod_datetime
      }}
      />
      <Helmet>
        <meta
          name="mg_search_action"
          content={
          // Mass.gov search engine results searchbox
            `
            {
              "target": "${siteUrl}/search?query={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          `}
        />
        <script type="application/ld+json">
          {
          // Google search engine results searchbox
            `
            {
              "@context": "https://schema.org",
              "@type": "WebSite",
              "url": "${siteUrl}",
              "potentialAction": {
                "@type": "SearchAction",
                "target": "${searchTarget}",
                "query-input": "required name=search_term_string"
              }
            }
          `}
        </script>
      </Helmet>
      <Layout {...layoutProps}>
        <Section>
          <p>
            {intro.paragraph}
          </p>
          {
            intro.links.map((link, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <GatsbyLink to={link.href} key={`introLinks.${i}`} type="decorative">{link.text}</GatsbyLink>
            ))
          }
        </Section>
        <Section>
          <section className="ma__sections-wrapper">
            <div className={`ma__sections-wrapper__container ma__sections-${sectionNum}up__container`}>
              { SectionCardProps.map((cardProps, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <SectionCard {...cardProps} index={i} key={`homepagecard-${i}`}>
                  {
                      cardProps.links.map((link, linkIndex) => {
                        const LinkComponent = components[link.component];
                        const linkProps = {
                          href: prefixInternalLinks(link.props.href),
                          text: link.props.text,
                          key: `SectionCard.${i}.LinkComponent.${linkIndex}`
                        };
                        return<LinkComponent {...linkProps} />;
                      })
                    }
                </SectionCard>
              ))}
            </div>
          </section>
        </Section>
        <Section>
          <Heading level={4} text={contactHeading} />
          {contactInformation.map((contact, contactIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <AccordionItem key={`ContactInfo.Accordion.${contactIndex}`} title={contact.title} info={contact.title} icon={null}>
              <ContactInformation {...contact} />
            </AccordionItem>
          ))}
        </Section>
      </Layout>
    </Fragment>
  );
};

IndexPage.propTypes = {
  data: PropTypes.object
};

export default IndexPage;

export const query = graphql`
  query IndexPageQuery {
    site {
      ...SiteMetadata
    }
    mainNav: navJson(navType: {eq: "header"}) {
    ...SiteNav
    }
    content: pagesJson(fields: {slug: {eq: "/"}}) {
      title
      header {
        title
        date(formatString: "MMMM D, YYYY")
        links {
          href
          text
        }
      }
      intro {
        paragraph
        links {
          href
          text
        }
      }
      sections {
        description
        # Comment out seeAll link in section cards
        #seeAll {
          #href
          #text
        #}
      }
      contactHeading
      # Get the contactinformation for the governors office
      ...contactInformation
      # Get the last modified timestamp for the file powering the page
      ...PagesJsonDataTimeStamp
    }
  }
`;
